/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { CloseRounded } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { QSTextInput } from '../../../../../../../shared/components/molecules';
import { chatListReducer } from '../../../../states/chat-list-reducer';
import { useReducerAtom } from 'jotai/utils';
import { chatListAtom, selectedChatIdsListAtom, toggleSelectedChatIdsAtom } from '../../../../states/chat-list';
import { ChatListReducerActionTypes } from '../../../../typings';
import debounce from '../../../../../../../shared/utils/debounce';
import { getOrganizationsSocket } from '../../../../../../../infra/sockets/organization-socket';
import useSocket from '../../../../../../../shared/hooks/use-socket';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { getMessageStatusesSocket, getNewMessageSocket } from '../../../../../../../infra/sockets';
import { selectedChatIdAtom } from '../../../../states/selected-chat';
import Loader from '../../../../../../../shared/components/molecules/loader';
import { isFilterAIAwaitingResponseEnabled } from '../../ai-filtering/state';
import { aiFeaturePermissionAtom } from '../../../chat-input/state';
import BulkActionIcons from '../bulk-action-icons';
import SearchIcon from '../../../../../../../shared/icons/icons-revamp/search';
import QSTooltip from '../../../../../../../shared/components/molecules/tooltip';

const Searchbar: React.FC<{
  customCSS?: Record<string, string>;
  placeholder?: string;
  isActive?: boolean;
  forwardMsgView?: boolean;
  showLoading?: boolean;
}> = ({ customCSS = {}, isActive, forwardMsgView, placeholder = 'Search... (Min 3 char.)', showLoading = false }) => {
  const chatId = useAtomValue(selectedChatIdAtom);

  const [
    { searchTerm, tags, waba, assignedUserId, status, slaStatus, leadCustomFieldIdList, forwardSearchTerm },
    dispatch,
  ] = useReducerAtom(chatListAtom, chatListReducer);

  const [inputValue, setInputValue] = useState(forwardMsgView ? forwardSearchTerm : searchTerm);
  const setToggleSelectedChatIds = useSetAtom(toggleSelectedChatIdsAtom);
  const [selectedChatIdsList, setSelectedChatIdsList] = useAtom(selectedChatIdsListAtom);

  const { socket: organisationSocket } = useSocket(getOrganizationsSocket);
  const { socket: conversationSocket } = useSocket(getNewMessageSocket, undefined, undefined, undefined, undefined, {
    doNotDisconnectOnUnmount: true,
    reconnectOnFocus: false,
    reconnectOnVisibility: false,
  });
  const isAiAwaitingReplyEnabled = useAtomValue(isFilterAIAwaitingResponseEnabled);
  const aiFeaturePermission = useAtomValue(aiFeaturePermissionAtom);

  const messageStatusSocket = useMemo(() => {
    if (chatId) return getMessageStatusesSocket(chatId);
  }, [chatId]);

  const emitFilters = useCallback(
    (value: string) => {
      const eventName = 'chat-filters-update';
      const payload: Record<string, string | string[] | undefined | null | {}> = {
        tags,
        status,
        slaStatus,
        assignedUserId:
          assignedUserId && assignedUserId !== 'null' ? assignedUserId : assignedUserId === 'null' ? null : undefined,
        waba,
        searchTerm: value,
        filterAiAwaitingResponse:
          isAiAwaitingReplyEnabled && aiFeaturePermission?.permissions?.chat_ai_awaiting_response,
      };

      if (leadCustomFieldIdList?.fieldId) {
        payload.customFields_v2 = [
          {
            id: leadCustomFieldIdList.listId,
            value: leadCustomFieldIdList.fieldId,
          },
        ];
      }

      organisationSocket.emit(eventName, payload);
      conversationSocket.emit(eventName, payload);
      if (messageStatusSocket) {
        messageStatusSocket.emit(eventName, payload);
      }
    },
    [
      tags,
      status,
      slaStatus,
      assignedUserId,
      waba,
      leadCustomFieldIdList,
      organisationSocket,
      conversationSocket,
      messageStatusSocket,
      isAiAwaitingReplyEnabled,
      aiFeaturePermission?.permissions?.chat_ai_awaiting_response,
    ]
  );

  const setSearch = useCallback(
    (value: string) => {
      if (forwardMsgView) {
        dispatch({ type: ChatListReducerActionTypes.FORWARD_SEARCH, data: value });
      } else {
        dispatch({ type: ChatListReducerActionTypes.SEARCH, data: value });
      }
      emitFilters(value);
    },
    [forwardMsgView, dispatch, emitFilters]
  );

  const debouncedInput = useMemo(
    () =>
      debounce((val: string) => {
        if (val.length === 0 || val.length >= 3) {
          setSearch(val);
        }
      }, 500),
    [setSearch]
  );

  useEffect(() => {
    debouncedInput(inputValue ? inputValue : '');
  }, [inputValue]);

  useEffect(() => {
    const newValue = forwardMsgView ? forwardSearchTerm : searchTerm;
    setInputValue(newValue);
  }, [forwardSearchTerm, searchTerm, forwardMsgView]);

  const handleOnClearClick = () => {
    setInputValue('');
    setSearch('');
    if (!forwardMsgView) {
      dispatch({ type: ChatListReducerActionTypes.SEARCH_STATUS, data: false });
    }
  };

  const showForwardCloseIcon = forwardMsgView && inputValue?.trim().length;
  const renderForwardEndAdornment = () => {
    if (!forwardMsgView || !showForwardCloseIcon) return null;

    return (
      <IconButton
        onClick={handleOnClearClick}
        sx={{
          padding: 0,
          marginRight: '2px',
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <CloseRounded sx={{ color: '#2d2d2d', fontSize: 20 }} />
      </IconButton>
    );
  };

  const renderForwardStartAdornment = () => {
    if (!forwardMsgView) return undefined;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '2px', marginRight: '8px' }}>
        <SearchIcon size={18} color="#2d2d2d" />
      </Box>
    );
  };

  const renderCloseIcon = () => {
    // For forward mode, the close icon is rendered inside the input (endAdornment).
    if (forwardMsgView) return null;

    return (
      <>
        <QSTooltip title="Close search" placement="top">
          <IconButton onClick={handleOnClearClick}>
            <CloseRounded sx={{ color: '#2d2d2d', fontSize: 20 }} />
          </IconButton>
        </QSTooltip>
        <BulkActionIcons />
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: forwardMsgView ? '40px' : '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <QSTextInput
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          ...(forwardMsgView
            ? {
                height: '40px',
                '.MuiInputBase-root': {
                  height: '40px',
                  minHeight: '40px',
                },
                '.MuiInputBase-input': {
                  height: '40px',
                  boxSizing: 'border-box',
                  lineHeight: '40px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                },
              }
            : {}),
          '.MuiFormControl-root': { margin: '0' },
          '.MuiInputBase-root::before': { borderBottom: 'none !important' },
          '.MuiInputBase-root,.MuiInput-root': {
            '&::before': { border: 'none' },
            '&::after': { border: 'none' },
            '&:hover:not(.Mui-disabled)::before': { border: 'none' },
            'input::placeholder, textArea::placeholder': { color: '#7B7B7B' },
            ...customCSS,
          },
        }}
        textInputProps={{ autoFocus: true }}
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        startAdornment={renderForwardStartAdornment()}
        endAdornment={renderForwardEndAdornment()}
      />
      {showLoading ? <Loader size={16} /> : null}
      {renderCloseIcon()}
    </Box>
  );
};

export default Searchbar;
